<script lang="ts" setup>

    const pageNames = ["home", "services", "machines", "about", "contact"];

    // To show the links
    const showLinks = ref(false)

    const menuButton = ref<HTMLButtonElement | null>(null)
    const closeButton = ref<HTMLButtonElement | null>(null)
    const menuList = ref<HTMLElement | null>(null)

    // Lock the page behind the fullscreen overlay so it cannot scroll underneath.
    useHead({
        bodyAttrs: {
            class: computed(() => (showLinks.value ? "menu-open" : "")),
        },
    })

    // Move focus into the overlay when it opens and hand it back to the
    // hamburger when it closes, so the menu is operable without a mouse.
    watch(showLinks, async (open) => {
        await nextTick()
        if (open) closeButton.value?.focus()
        else menuButton.value?.focus()
    })

    // Keep Tab inside the overlay while it is open; everything behind it is
    // covered but still in the tab order otherwise.
    function trapTab(event: KeyboardEvent) {
        const focusable = menuList.value?.querySelectorAll<HTMLElement>("a[href], button")
        if (!focusable?.length) return

        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    function onKeydown(event: KeyboardEvent) {
        if (!showLinks.value) return

        if (event.key === "Escape") showLinks.value = false
        else if (event.key === "Tab") trapTab(event)
    }

    onMounted(() => window.addEventListener("keydown", onKeydown))
    onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown))

</script>

<template>

    <nav :aria-label="$t('nav.main_menu')">

        <div class="logo">
            <a :href="`#${getAnchorName('home')}`">
                <img src="@/assets/images/smit-logo.png" width="470" height="531" :alt="`${$t('company_name')} Logo`"/>
            </a>
        </div>
        <button
            ref="menuButton"
            class="menu-button"
            :class="{'active': showLinks}"
            :aria-label="showLinks ? $t('nav.close_menu') : $t('nav.open_menu')"
            :aria-expanded="showLinks"
            aria-controls="main-menu"
            @click="showLinks = !showLinks"
        >
            <Icon name="ic:round-menu" aria-hidden="true"></Icon>
        </button>

        <ul id="main-menu" ref="menuList" class="page-links" :class="{'show': showLinks }">

            <!-- Logo in the menubar if needed. -->
            <li class="logo">
                <a :href="`#${getAnchorName('home')}`" @click="showLinks = false">
                    <img src="@/assets/images/smit-logo.png" width="470" height="531" :alt="`${$t('company_name')} Logo`"/>
                </a>
            </li>

            <!-- Language selector -->
            <LanguageSelector></LanguageSelector>

            <!-- All the page names and links -->
            <li v-for="page of pageNames" :key="page">
                <a :href="`#${getAnchorName(page)}`" @click="showLinks = false">{{ $t(`sections.${page}`) }}</a>
            </li>

            <li class="close-menu">
                <button ref="closeButton" :aria-label="$t('nav.close_menu')" @click="showLinks = false">
                    <Icon name="material-symbols-light:close" aria-hidden="true"></Icon>
                </button>
            </li>

        </ul>
    </nav>

</template>

<style lang="css" scoped>

    .language-selector {
        position: absolute;
        top: 0em;
        right: 0em;
    }

    nav {
        width: 100%;
        box-sizing: border-box;
        padding: 2em 3em;
        justify-content: space-between;
        align-items: center;
        display: flex;
    }

    img {
        height: 5em;
        width: auto;
        cursor: pointer;
    }

    li {
        list-style: none;
        cursor: pointer;
    }


    a {
        text-decoration: none;
        color: var(--black-text-color);
        transition: color 0.33s ease-in-out;
    }

    li:hover, a:hover {
        color: var(--green-accent-color);
    }

    ul.page-links {
        display: flex;
        gap: 4em;
        font-size: 1em;
        font-family: "Rubik";
    }

    button {
        padding: 0em;
        background: none;
        border: none;
        /* reset.css does not cover button, which otherwise keeps the UA font. */
        font: inherit;
        color: inherit;
        cursor: pointer;
        display: block;
    }

    button.menu-button {
        /* Fixed, so the menu stays reachable once the page is scrolled. */
        position: fixed;
        padding: 0.25em;
        padding-right: 0.5em;
        right: 0em;
        top: 2em;
        z-index: 50;
        font-size: 2em;
        background: #e3e3e3;
        color: black;
        transition: background 0.33s ease-in-out;
    }

    /* Open state only — driven by showLinks, not by :focus, which would
       leave the button highlighted after the menu is closed again. */
    button.menu-button.active {
        background: var(--green-accent-color);
        color: white;
    }

    .menu-button:focus-visible,
    .close-menu button:focus-visible,
    ul.page-links a:focus-visible {
        outline: 2px solid var(--green-accent-color);
        outline-offset: 2px;
    }

    /* Only shown in the mobile overlay. */
    button.menu-button,
    .close-menu,
    li.logo {
        display: none;
    }


    /* Below 800px */
    @media (max-width: 800px) {

        nav {
            flex-direction: column;
            gap: 1em;
            padding-bottom: 0px;
            padding-top: 3vh;
        }

        ul.page-links {
            flex-direction: column;
            gap: 2em;
            padding-top: 2em;
            justify-content: flex-start;
            text-align: center;

            position: fixed;
            inset: 0;
            background: #e3e3e3;
            box-sizing: border-box;
            overflow-y: auto;
            z-index: 100;

            font-size: 1.5em;

            /* transform instead of margin: composited, no reflow per frame.
               visibility is delayed until the slide-out has finished. */
            transform: translateY(100%);
            visibility: hidden;
            transition: transform 0.4s cubic-bezier(0.215, 0.610, 0.355, 1),
                        visibility 0s linear 0.4s;
        }

        ul.page-links.show {
            transform: translateY(0);
            visibility: visible;
            transition: transform 0.4s cubic-bezier(0.215, 0.610, 0.355, 1),
                        visibility 0s;
        }

        @media (prefers-reduced-motion: reduce) {
            ul.page-links,
            ul.page-links.show {
                transition: none;
            }
        }

        img {
            height: 7.5em;
        }

        button.menu-button {
            display: flex;
        }

        li.logo {
            display: block;
        }

        li.logo img {
            height: 4em;
        }

        .close-menu {
            display: block;
            font-size: 1.5em;
        }

        .close-menu button {
            margin: 0 auto;
        }

        .language-selector {
            position: relative;
            top: unset;
            right: unset;
            align-self: center;
            padding: 0;
            font-size: 1.1em;
            margin-top: -1em;
        }

    }


</style>
